/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : actionHandler.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 06-07-2024
 * @description This file is to handle all type of actions & handle the toast message for response as well
 */

import { toast } from "react-toastify";
import customConsole from "../../config/customConsole";
import useAxios from "../../hooks/useAxios";
import { startLoading, stopLoading } from "./spinner";

const baseURL = process.env.REACT_APP_BASE_URL;
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 06-07-2024
 * @description Function to display the toast message
 * @param {type,message}
 * @returns {toast message}
 */
export function showToastMessage({ type, message }) {
  switch (type) {
    case "info":
      toast.info(message);
      break;
    case "warn":
      toast.warn(message);
      break;
    case "success":
      toast.success(message);
      break;
    case "error":
      toast.error(message);
      break;
    default:
      toast(message);
  }
}

const buildAxiosConfig = (props) => ({
  ...(props.ignore_app_loading_spinner === true && {
    ignoreAppLoadingSpinner: true,
  }),
});

const resolveUrl = (props) =>
  props.api_name.startsWith("http")
    ? props.api_name
    : `${baseURL}${props.api_name}`;

const createDefaultResp = () => ({
  error: true,
  msg: "",
  data: { error: true, msg: "", data: {} },
});

const performApiCall = async ({ api, props, resolvedUrl, axiosConfig }) => {
  const config = {
    ...axiosConfig,
    ...(props.headers && { headers: props.headers }),
  };

  switch (props.https_method) {
    case "GET":
      if (props.is_file) {
        return api.get(resolvedUrl, {
          params: { filename: props.file_name },
          responseType: "blob",
          ...config,
        });
      }
      if (props.is_get_file) {
        return api.get(resolvedUrl, {
          params: props.params,
          responseType: "blob",
          ...config,
        });
      }
      return api.get(resolvedUrl, { params: props.params, ...config });
    case "DELETE":
      return api.delete(`${baseURL}${props.api_name}`, {
        ...config,
        data: { ...props.params },
      });
    case "PUT":
      return api.put(resolvedUrl, { ...props.params }, config);
    case "POST":
      if (props.is_file) {
        const file = new FormData();
        file.append("name", "filename");
        file.append("filename", props.file);
        return api.post(resolvedUrl, file, config);
      }
      return api.post(resolvedUrl, { ...props.params }, config);
    case "PATCH":
      return api.patch(resolvedUrl, { ...props.params }, config);
    default:
      console.warn("Unhandled HTTP method:", props.https_method);
      return null;
  }
};

const handleResponse = ({ props, resp, dispatch }) => {
  if (!props.action_type) return resp.data;

  if (props.is_file && props.https_method === "GET") {
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    dispatch({
      type: props.action_type,
      payload: { error: false, msg: "File downloaded", file_path: url },
    });
    return resp.data;
  }

  const adaptedPayload = props.adapter ? props.adapter(resp) : resp.data;
  dispatch({
    type: props.action_type,
    payload: {
      error: resp?.data?.error ?? false,
      msg: resp?.data?.msg ?? "",
      ...adaptedPayload,
    },
  });

  return resp.data;
};

const handleError = ({ error, props }) => {
  customConsole("-- API catch block --");
  customConsole(error);
  if (props.show_toast && error?.response?.data?.msg) {
    showToastMessage({ type: "error", message: error.response.data.msg });
  }
};
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 06-07-2024
 * @description Handle different type of actions with the useAxios custom Hook
 * @param {api, https_method, params, show_toast, action_type, is_file}
 * @returns {dispatch} dipatch the action to the reducer
 */
export function HandleApiActions(props) {
  const api = useAxios();
  customConsole("-- HandleApiActions --");
  customConsole(props);

  return async (dispatch) => {
    const shouldShowSpinner = props.ignore_app_loading_spinner !== true;
    let resp = createDefaultResp();
    try {
      if (shouldShowSpinner) {
        console.log("---loading---");
        dispatch(startLoading());
      }
      let apiActionPerformed = true;
      const axiosConfig = buildAxiosConfig(props);
      console.log("axiosConfig", axiosConfig);
      const resolvedUrl = resolveUrl(props);
      resp = await performApiCall({ api, props, resolvedUrl, axiosConfig });
      if (!resp) {
        apiActionPerformed = false;
        resp = createDefaultResp();
      }

      const data = handleResponse({ props, resp, dispatch });

      if (apiActionPerformed) {
        if (props.show_toast && resp.data.msg) {
          showToastMessage({
            type: resp.data.error ? "error" : "success",
            message: resp.data.msg,
          });
        }
        // Call the callback if provided
        if (typeof props.callback === "function") {
          props.callback(resp.data);
        }
      }
      return data;
    } catch (error) {
      handleError({ error, props });
      return resp.data;
    } finally {
      // STOP SPINNER (ALWAYS)
      if (shouldShowSpinner) {
        dispatch(stopLoading());
      }
    }
    return resp.data;
  };
  // const performApiCall = async ({ props, resolvedUrl, axiosConfig }) => {
  //   switch (props.https_method) {
  //     case "GET":
  //       if (props.is_file) {
  //         return api.get(resolvedUrl, {
  //           params: { filename: props.file_name },
  //           responseType: "blob",
  //           ...axiosConfig,
  //         });
  //       }

  //       if (props.is_get_file) {
  //         return api.get(resolvedUrl, {
  //           params: props.params,
  //           responseType: "blob",
  //           ...axiosConfig,
  //         });
  //       }

  //       return api.get(resolvedUrl, {
  //         params: props.params,
  //         ...axiosConfig,
  //       });

  //     case "DELETE":
  //       return api.delete(resolvedUrl, {
  //         data: { ...props.params },
  //         ...axiosConfig,
  //       });

  //     case "PUT":
  //       return api.put(resolvedUrl, { ...props.params }, axiosConfig);

  //     case "POST":
  //       if (props.is_file) {
  //         const file = new FormData();
  //         file.append("name", "filename");
  //         file.append("filename", props.file);
  //         return api.post(resolvedUrl, file);
  //       }

  //       return api.post(resolvedUrl, { ...props.params }, axiosConfig);

  //     case "PATCH":
  //       return api.patch(resolvedUrl, { ...props.params }, axiosConfig);

  //     default:
  //       console.warn("Unhandled HTTP method:", props.https_method);
  //       return null;
  //   }
  // };
  // const handleResponse = ({ props, resp, dispatch }) => {
  //   if (!props.action_type) return resp.data;

  //   // FILE DOWNLOAD CASE
  //   if (props.is_file && props.https_method === "GET") {
  //     const url = window.URL.createObjectURL(new Blob([resp.data]));

  //     dispatch({
  //       type: props.action_type,
  //       payload: {
  //         error: false,
  //         msg: "File downloaded",
  //         file_path: url,
  //       },
  //     });

  //     return resp.data;
  //   }

  //   // NORMAL RESPONSE
  //   const adaptedPayload = props.adapter ? props.adapter(resp) : resp.data;

  //   dispatch({
  //     type: props.action_type,
  //     payload: {
  //       error: resp?.data?.error ?? false,
  //       msg: resp?.data?.msg ?? "",
  //       ...adaptedPayload,
  //     },
  //   });

  //   return resp.data;
  // };
  // const handleError = ({ error, props }) => {
  //   customConsole("-- API catch block --");
  //   customConsole(error);

  //   if (props.show_toast && error?.response?.data?.msg) {
  //     showToastMessage({
  //       type: "error",
  //       message: error.response.data.msg,
  //     });
  //   }
  // };
  // return async (dispatch) => {
  //   const shouldShowSpinner = props.ignore_app_loading_spinner !== true;

  //   if (shouldShowSpinner) {
  //     dispatch(startLoading());
  //   }

  //   const axiosConfig = {
  //     ...(props.ignore_app_loading_spinner === true && {
  //       ignoreAppLoadingSpinner: true,
  //     }),
  //   };

  //   const resolvedUrl = props.api_name.startsWith("http")
  //     ? props.api_name
  //     : `${baseURL}${props.api_name}`;

  //   let resp;

  //   try {
  //     resp = await performApiCall({ props, resolvedUrl, axiosConfig });

  //     if (!resp) return;

  //     const data = handleResponse({ props, resp, dispatch });

  //     // Toast
  //     if (props.show_toast && resp?.data?.msg) {
  //       showToastMessage({
  //         type: resp.data.error ? "error" : "success",
  //         message: resp.data.msg,
  //       });
  //     }

  //     // Callback
  //     if (typeof props.callback === "function") {
  //       props.callback(resp.data);
  //     }

  //     return data;
  //   } catch (error) {
  //     handleError({ error, props });
  //     return resp?.data;
  //   } finally {
  //     if (shouldShowSpinner) {
  //       dispatch(stopLoading());
  //     }
  //   }
  // };
}
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 31/07/2024
 * @description API handler to get Multiple Images at once
 * @param {[{id:1, image:""}]}
 * @returns {details:[{id:1, image:"", url:""}]}
 */
export async function GetMultipleImages(props) {
  const api = useAxios();
  customConsole("-- GetMultipleImages --");
  customConsole(props);
  try {
    const axiosConfig = {
      ...(props.ignore_app_loading_spinner === true && {
        ignoreAppLoadingSpinner: true,
      }),
    };
    const result = [];
    //Based on type of API perform the action
    for (const imageDetails of props) {
      let resp = { data: "" };
      if (imageDetails.image.length) {
        resp = await api.get(
          `${baseURL}/UCM/files/details`,
          {
            params: { filename: imageDetails.image },
            responseType: "blob", // Important for handling binary data
          },
          axiosConfig,
        );
      }
      result.push({
        id: imageDetails.id,
        image: imageDetails.image,
        url: resp?.data
          ? window.URL.createObjectURL(new Blob([resp.data]))
          : "",
      });
    }
    return result;
  } catch (error) {
    customConsole("-- API catch block --");
    customConsole(error);
    if (
      props.show_toast &&
      error.response &&
      error.response.data &&
      error.response.data.msg
    ) {
      showToastMessage({ type: "error", message: error.response.data.msg });
    }
  }
}
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 31/07/2024
 * @description API handler to get Single Image at once
 * @param { image:""}
 * @returns {url:""}}
 */
export async function GetSingleImages(props) {
  const api = useAxios();
  customConsole("-- GetSingleImages --");
  customConsole(props);
  try {
    const axiosConfig = {
      ...(props.ignore_app_loading_spinner === true && {
        ignoreAppLoadingSpinner: true,
      }),
    };
    //Based on type of API perform the action
    const resp = await api.get(
      `${baseURL}/UCM/files/details`,
      {
        params: { filename: props.image },
        responseType: "blob", // Important for handling binary data
      },
      axiosConfig,
    );
    return resp?.data ? window.URL.createObjectURL(new Blob([resp.data])) : "";
  } catch (error) {
    customConsole("-- API catch block --");
    customConsole(error);
    if (
      props.show_toast &&
      error.response &&
      error.response.data &&
      error.response.data.msg
    ) {
      showToastMessage({ type: "error", message: error.response.data.msg });
    }
  }
}
